import {
  getRouteApi,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { format as formatTZ, toZonedTime } from "date-fns-tz";
import { useEffect } from "react";
import { PageHeader } from "@/components/layouts/PageHeader";
import ShareOfVoiceContent from "@/components/share-of-voice/ShareOfVoiceContent";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import {
  calculateDateRangeFromDays,
  formatDateForUrl,
  parseDateString,
} from "@/lib/utils/dateUtils";
import { buildAndFromSelected } from "@/lib/utils/filters";
import ShareOfVoiceFilterSideBar from "@/pages/share-of-voice/ShareOfVoiceFilterSideBar";
import { logoutUser } from "@/store/auth.async-actions";
import useAuth, { type AuthState } from "@/store/auth.slice";
import { ShareOfVoiceParamsProvider } from "@/store/share-of-voice/params/shareOfVoice-params.context";
import { getSegments } from "@/store/share-of-voice/segment/segment-async.actions";

const routeApi = getRouteApi("/_authenticated/share-of-voice");

/**
 * Computes effective start/end dates from search params.
 * If custom dates provided, use those. Otherwise calculate from filterDays.
 */
const computeDates = (
  filterDays: number,
  startDateParam?: string,
  endDateParam?: string,
): { startDate: string; endDate: string; isCustomDaysRange: boolean } => {
  const timeZone = "UTC";

  if (startDateParam && endDateParam) {
    return {
      startDate: startDateParam,
      endDate: endDateParam,
      isCustomDaysRange: true,
    };
  }

  const { startDate, endDate } = calculateDateRangeFromDays(filterDays);
  return {
    startDate: formatTZ(toZonedTime(startDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    endDate: formatTZ(toZonedTime(endDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    isCustomDaysRange: false,
  };
};

/**
 * Converts TanStack Router `selected` Record to a Map for child components.
 * Accepts partial record (Zod 4 partialRecord); missing keys are treated as empty array.
 */
const toSelectedFiltersMap = (
  selected: Partial<Record<string, string[]>>,
): Map<ShareVoiceSidebarCategory, string[]> => {
  return new Map(
    (
      Object.entries(selected) as [
        ShareVoiceSidebarCategory,
        string[] | undefined,
      ][]
    ).map(([k, v]) => [k, v ?? []] as [ShareVoiceSidebarCategory, string[]]),
  );
};

/**
 * Share of Voice route component for TanStack Router.
 * Uses TanStack Router search params as source of truth for filters.
 */
const ShareOfVoiceRoute = () => {
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const user = useAuth((state: AuthState) => state.user);

  useEffect(() => {
    getSegments();
  }, []);

  const { startDate, endDate, isCustomDaysRange } = computeDates(
    searchParams.filterDays,
    searchParams.startDate,
    searchParams.endDate,
  );

  const selectedFilters = toSelectedFiltersMap(searchParams.selected);
  const filtersAnd = buildAndFromSelected(searchParams.selected);

  /** Updates URL search params when date range changes */
  const handleDateRangeChange = (range: {
    from?: Date;
    to?: Date;
    days?: number;
    isCustom?: boolean;
  }) => {
    if (range.isCustom && range.from && range.to) {
      navigate({
        to: pathname,
        search: (prev) => ({
          ...prev,
          startDate: formatDateForUrl(range.from as Date),
          endDate: formatDateForUrl(range.to as Date),
          filterDays: prev.filterDays,
        }),
      });
    } else if (range.days) {
      navigate({
        to: pathname,
        search: (prev) => ({
          ...prev,
          filterDays: range.days as number,
          startDate: undefined,
          endDate: undefined,
        }),
      });
    }
  };

  /** Updates URL search params when a filter is toggled */
  const handleToggleFilterSelection = (
    category: ShareVoiceSidebarCategory,
    item: string | number,
  ) => {
    navigate({
      to: pathname,
      search: (prev) => {
        const current = prev.selected?.[category] ?? [];
        const itemStr = String(item);
        const updated = current.includes(itemStr)
          ? current.filter((v) => v !== itemStr)
          : [...current, itemStr];

        const newSelected = { ...prev.selected };
        if (updated.length > 0) {
          newSelected[category] = updated;
        } else {
          delete newSelected[category];
        }

        return { ...prev, selected: newSelected };
      },
    });
  };

  const paramsContextValue = {
    startDate,
    endDate,
    filtersAnd,
  };

  return (
    <>
      <PageHeader
        label="Dynamic Share of Voice"
        userEmail={user?.email ?? ""}
        userTenantName={user?.default_tenant?.name}
        onLogout={logoutUser}
      />

      <ShareOfVoiceParamsProvider value={paramsContextValue}>
        <main className="flex h-[calc(100vh-57px)]">
          <ShareOfVoiceFilterSideBar
            selectedFilters={selectedFilters}
            toggleFilterSelection={handleToggleFilterSelection}
          />
          <div className="flex-1 bg-white overflow-y-auto overflow-x-hidden min-w-0">
            <ShareOfVoiceContent
              filterDays={searchParams.filterDays}
              isCustomDaysRange={isCustomDaysRange}
              startDate={parseDateString(searchParams.startDate)}
              endDate={parseDateString(searchParams.endDate)}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        </main>
      </ShareOfVoiceParamsProvider>
    </>
  );
};

export default ShareOfVoiceRoute;
